import type {RouteRecordRaw} from 'vue-router';
import type {CommonRouteMeta} from '@/types/common';

const BaseDashboard = () => import('@/views/dashboard/BaseDashboard.vue');

const superAdminRoutes: RouteRecordRaw = {
    path: '/super-admin',
    component: BaseDashboard,
    meta: {
        title: 'Super Admin',
        icon: 'mdi-login',
        noCache: false,
        superAdmin: true,
        hidden: true,
        middleware: ['auth', 'superAdmin'],
        layout: 'Dashboard',
        roles: ['super-admin']
    } as CommonRouteMeta,
    children: [
        {
            path: '',
            name: 'super.admin.general',
            component: () => import('@/views/dashboard/superAdmin/GeneralSuperAdmin.vue'),
        },
        {
            path: 'roles',
            component: () => import('@/views/dashboard/superAdmin/rolesPermissions/RolesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.roles',
                    component: () => import('@/views/dashboard/superAdmin/rolesPermissions/Roles.vue'),
                },
                {
                    path: 'new',
                    name: 'super.admin.roles.new',
                    component: () => import('@/views/dashboard/superAdmin/rolesPermissions/RolesNew.vue'),
                },
                {
                    path: ':role_id',
                    name: 'super.admin.roles.edit',
                    component: () => import('@/views/dashboard/superAdmin/rolesPermissions/RolesEdit.vue'),
                },
            ],
        },
        {
            path: 'permissions',
            component: () => import('@/views/dashboard/superAdmin/rolesPermissions/PermissionsBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.permissions',
                    component: () => import('@/views/dashboard/superAdmin/rolesPermissions/Permissions.vue'),
                },
                {
                    path: 'new',
                    name: 'super.admin.permissions.new',
                    component: () => import('@/views/dashboard/superAdmin/rolesPermissions/PermissionsNew.vue'),
                },
                {
                    path: ':permission_id',
                    name: 'super.admin.permissions.edit',
                    component: () => import('@/views/dashboard/superAdmin/rolesPermissions/PermissionsEdit.vue'),
                },
            ],
        },
        {
            path: 'media',
            name: 'super.admin.media',
            component: () => import('@/views/dashboard/superAdmin/media/Media.vue'),
        },
        {
            path: 'media/upload',
            name: 'super.admin.media.upload',
            component: () => import('@/views/dashboard/superAdmin/media/SUUploadMedia.vue'),
        },
        {
            path: 'users',
            component: () => import('@/views/dashboard/superAdmin/users/UsersBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.users',
                    component: () => import('@/views/dashboard/superAdmin/users/Users.vue'),
                },
                {
                    path: ':user_id',
                    name: 'super.admin.users.edit',
                    component: () => import('@/views/dashboard/superAdmin/users/UsersEdit.vue'),
                },
            ],
        },
        {
            path: 'masquerade',
            name: 'super.admin.users.masquerade',
            component: () => import('@/views/dashboard/superAdmin/users/UserMasquerade.vue'),
        },
        {
            path: 'companies',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.companies',
                    component: () => import('@/views/dashboard/superAdmin/companies/Companies.vue'),
                },
                {
                    path: ':company_id',
                    name: 'super.admin.company.edit',
                    component: () => import('@/views/dashboard/superAdmin/companies/CompanyEdit.vue'),
                },
            ],
        },
        {
            path: 'spam-companies',
            name: 'super.admin.spam-companies',
            component: () => import('@/views/dashboard/superAdmin/companies/SpamCompanies.vue'),
        },
        {
            path: 'stripe-cleanup',
            name: 'super.admin.stripe-cleanup',
            component: () => import('@/views/dashboard/superAdmin/companies/StripeCleanup.vue'),
        },
        {
            path: 'stats',
            component: () => import('@/views/dashboard/superAdmin/stats/StatsBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.stats',
                    component: () => import('@/views/dashboard/superAdmin/stats/StatsPage.vue'),
                },
                {
                    path: 'users',
                    name: 'super.admin.stats.users',
                    component: () => import('@/views/dashboard/superAdmin/stats/UserStats.vue'),
                },
                {
                    path: 'subscribers',
                    name: 'super.admin.stats.subscribers',
                    component: () => import('@/views/dashboard/superAdmin/stats/SubscriberStats.vue'),
                },
            ],
        },
        {
            path: 'automations',
            name: 'super.admin.automations',
            component: () => import('@/views/dashboard/superAdmin/operations/EmailAutomations.vue'),
        },
        {
            path: 'whats-new',
            component: () => import('@/views/dashboard/superAdmin/stats/StatsBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.whats-new',
                    component: () => import('@/views/dashboard/superAdmin/whats-new/WhatsNew.vue'),
                },
            ],
        },
        {
            path: 'server',
            name: 'super.admin.server',
            component: () => import('@/views/dashboard/superAdmin/server/ServerBase.vue'),
        },
        {
            path: 'utils',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.utils',
                    component: () => import('@/views/dashboard/superAdmin/utils/UtilsBase.vue'),
                },
            ],
        },
        {
            path: 'push-notifications',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.push-notifications',
                    component: () => import('@/views/dashboard/superAdmin/companies/PushNotifications.vue'),
                },
            ],
        },
        {
            path: 'booking-images',
            name: 'super.admin.booking-images',
            component: () => import('@/views/dashboard/superAdmin/BookingImages.vue'),
        },
        {
            path: 'search-insights',
            name: 'super.admin.search-insights',
            component: () => import('@/views/dashboard/superAdmin/SearchInsights.vue'),
        },
        {
            path: 'features',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.features',
                    component: () => import('@/views/dashboard/superAdmin/companies/Features.vue'),
                },
            ],
        },
        {
            path: 'affiliate',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.affiliate',
                    component: () => import('@/views/dashboard/superAdmin/companies/Affiliate.vue'),
                },
            ],
        },
        {
            path: 'cache',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.cache',
                    component: () => import('@/views/dashboard/superAdmin/companies/Cache.vue'),
                },
            ],
        },
        {
            path: 'account-health',
            name: 'super.admin.account-health',
            component: () => import('@/views/dashboard/superAdmin/AccountHealth.vue'),
        },
        {
            path: 'caddy',
            name: 'super.admin.caddy',
            component: () => import('@/views/dashboard/superAdmin/CaddyAdmin.vue'),
        },
        {
            path: 'caddy-domains',
            redirect: () => ({name: 'super.admin.caddy', query: {tab: 'domains'}}),
        },
        {
            path: 'caddy-certificates',
            redirect: () => ({name: 'super.admin.caddy', query: {tab: 'certificates'}}),
        },
        {
            path: 'queues',
            name: 'super.admin.queues',
            component: () => import('@/views/dashboard/superAdmin/operations/QueuesStatus.vue'),
        },
        {
            path: 'masquerade-audit',
            name: 'super.admin.masquerade-audit',
            component: () => import('@/views/dashboard/superAdmin/operations/MasqueradeAudit.vue'),
        },
        {
            path: 'feature-audit',
            name: 'super.admin.feature-audit',
            component: () => import('@/views/dashboard/superAdmin/operations/FeatureAudit.vue'),
        },
        {
            path: 'billing-health',
            name: 'super.admin.billing-health',
            component: () => import('@/views/dashboard/superAdmin/operations/BillingHealth.vue'),
        },
        {
            path: 'integrations',
            name: 'super.admin.integrations',
            component: () => import('@/views/dashboard/superAdmin/operations/Integrations.vue'),
        },
        {
            path: 'scheduler',
            name: 'super.admin.scheduler',
            component: () => import('@/views/dashboard/superAdmin/operations/Scheduler.vue'),
        },
        {
            path: 'audit-log',
            name: 'super.admin.audit-log',
            component: () => import('@/views/dashboard/superAdmin/operations/AuditLog.vue'),
        },
        {
            path: 'broadcasts',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.broadcasts',
                    component: () => import('@/views/dashboard/superAdmin/Broadcasts.vue'),
                },
            ],
        }, {
            path: 'pdf-cert-signing',
            component: () => import('@/views/dashboard/superAdmin/certSigning/CertSigningBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.pdf-cert-signing',
                    component: () => import('@/views/dashboard/superAdmin/certSigning/CertSigningTest.vue'),
                },
            ],
        },
        {
            path: 'affiliate/:uuid',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.affiliateSingle',
                    component: () => import('@/views/dashboard/superAdmin/companies/AffiliateSingle.vue'),
                },
            ],
        },
        {
            path: 'bulk-email',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.bulk-email',
                    component: () => import('@/views/dashboard/superAdmin/bulkEmail/BulkEmail.vue'),
                },
            ],
        },
        {
            path: 'mobile-beta',
            component: () => import('@/views/dashboard/superAdmin/companies/CompaniesBase.vue'),
            children: [
                {
                    path: '',
                    name: 'super.admin.mobile-beta',
                    component: () => import('@/views/dashboard/superAdmin/mobileBeta/MobileBetaAdmin.vue'),
                },
            ],
        },
    ],
};

export default superAdminRoutes;
